import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    document.title = '关于我们 - 韩国漫画大全 | 免费在线韩漫阅读网';
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '韩国漫画大全',
    url: typeof window !== 'undefined' ? window.location.origin : '',
    description: '韩国漫画大全网收录超万部韩漫作品，全部免费在线阅读，是韩漫爱好者的一站式阅读平台。',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@kcomic.example.com',
      availableLanguage: ['Chinese']
    }
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '韩国漫画大全网站上的漫画是否完全免费？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的，韩国漫画大全网站上的所有韩漫作品全部免费在线阅读，无需付费订阅，也没有任何隐藏费用。我们致力于为广大韩漫爱好者提供一个完全免费的阅读平台。'
        }
      },
      {
        '@type': 'Question', 
        name: '网站多久更新一次漫画内容？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '韩国漫画大全网站每天都会更新最新的韩漫内容。连载中的作品会保持与韩国同步的更新频率，确保读者能够在第一时间阅读到最新的章节。'
        }
      },
      {
        '@type': 'Question',
        name: '可以在手机上阅读韩漫大全的漫画吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '当然可以。韩国漫画大全网站全面适配移动端，您可以使用手机、平板等任何设备随时随地进行阅读。我们采用了响应式设计，确保在不同屏幕尺寸上都能获得良好的阅读体验。'
        }
      },
      {
        '@type': 'Question',
        name: '如何找到我想看的韩漫？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过以下几种方式找到心仪的韩漫：1）在首页顶部的搜索框直接搜索漫画名称；2）通过分类浏览页面，按热血、恋爱、奇幻、悬疑等八大分类筛选；3）浏览热门推荐、最新更新等板块发现新作品。'
        }
      },
      {
        '@type': 'Question',
        name: '网站收录了多少部韩漫作品？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '韩国漫画大全目前收录超过10,000部韩漫作品，涵盖热血、恋爱、奇幻、悬疑、搞笑、动作、校园、都市等八大分类，并且每天都在持续增加新的作品。'
        }
      }
    ]
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      <main>
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="px-4 md:px-6 py-8">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb items={[{ name: '关于我们' }]} />
              <h1 className="mt-4 text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                关于我们
              </h1>
              <p className="text-sm text-foreground-500 mt-2">了解韩漫大全的故事与使命</p>
            </div>
          </div>
        </div>

        <section className="px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-primary-100 text-primary-600 mx-auto mb-4">
                <i className="ri-book-open-line text-2xl"></i>
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                品牌故事
              </h2>
            </div>

            <div className="prose prose-sm max-w-none">
              <div className="bg-background-100 rounded-lg p-6 md:p-8 mb-8">
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  <strong>韩国漫画大全</strong>诞生于对韩漫文化的热爱。我们是一群资深的韩漫爱好者，深知许多读者在寻找高质量、免费且更新及时的韩漫阅读平台时的困扰。于是，我们创立了这个平台，致力于为中文读者打造一个一站式的韩漫阅读天堂。
                </p>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  从创立之初至今，韩漫大全已经收录了超过<strong>10,000部</strong>韩漫作品，涵盖热血、恋爱、奇幻、悬疑、搞笑、动作、校园、都市等<strong>八大分类</strong>。无论是正在热更的连载作品，还是值得回味的完结经典，你都能在这里找到。
                </p>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  我们的核心理念是<strong>"让每一位韩漫爱好者都能免费享受优质阅读"</strong>。未来，我们将继续努力，持续扩充漫画资源库，优化阅读体验，让韩漫大全成为中文世界最受欢迎的韩漫阅读平台。
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-accent-100 text-accent-600 mx-auto mb-4">
                <i className="ri-star-line text-2xl"></i>
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                用户口碑
              </h2>
              <p className="text-sm text-foreground-500 mt-2">听听读者们怎么说</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
              {[
                {
                  name: '小明同学',
                  role: '韩漫爱好者，3年老用户',
                  comment: '韩漫大全真的是我用过最好的韩漫阅读网站了！资源全、更新快，最重要的是完全免费。从《我独自升级》到《全知读者视角》，在这里追了好多部经典作品。',
                  rating: 5
                },
                {
                  name: '爱吃泡菜的猫',
                  role: '韩漫新粉，使用半年',
                  comment: '作为一个刚入坑韩漫的新人，韩漫大全的分类导航帮了我大忙。不知道看什么的时候就去热门推荐逛逛，每次都能发现好作品。网站的界面也很清爽，阅读体验很舒适。',
                  rating: 5
                },
                {
                  name: '追更少年',
                  role: '资深漫画读者',
                  comment: '我追韩漫已经五年多了，韩漫大全是目前中文圈里更新最快最全的。完结区也很棒，补了好多经典老番。推荐给了身边所有的朋友，用了都说好！',
                  rating: 5
                }
              ].map((review) => (
                <div key={review.name} className="p-5 md:p-6 rounded-lg bg-background-50">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: review.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-amber-400 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    "{review.comment}"
                  </p>
                  <div>
                    <p className="text-sm font-semibold text-foreground-950">{review.name}</p>
                    <p className="text-xs text-foreground-500">{review.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-secondary-100 text-secondary-600 mx-auto mb-4">
                <i className="ri-mail-line text-2xl"></i>
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                联系我们
              </h2>
              <p className="text-sm text-foreground-500 mt-2">如果你有任何问题或建议，欢迎随时联系我们</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-8">
              <div className="p-5 rounded-lg bg-background-100 text-center">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mx-auto mb-3">
                  <i className="ri-mail-send-line"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-950 mb-1">电子邮箱</h3>
                <p className="text-xs text-foreground-500">contact@kcomic.example.com</p>
              </div>
              <div className="p-5 rounded-lg bg-background-100 text-center">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mx-auto mb-3">
                  <i className="ri-wechat-line"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-950 mb-1">微信公众号</h3>
                <p className="text-xs text-foreground-500">韩漫大全</p>
              </div>
              <div className="p-5 rounded-lg bg-background-100 text-center">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 mx-auto mb-3">
                  <i className="ri-weibo-line"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-950 mb-1">微博</h3>
                <p className="text-xs text-foreground-500">@韩漫大全官方微博</p>
              </div>
            </div>

            <div className="max-w-md mx-auto bg-background-100 rounded-lg p-6">
              <h3 className="text-base font-semibold text-foreground-950 mb-4 text-center">发送消息</h3>
              <form
                data-readdy-form
                className="flex flex-col gap-3"
                onSubmit={async (e) => {
                  e.preventDefault();
                  const form = e.currentTarget;
                  const formData = new FormData(form);

                  const honeypot = (form.querySelector('[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
                  if (honeypot) {
                    setFormStatus('success');
                    return;
                  }

                  try {
                    const res = await fetch('https://readdy.ai/api/form/d9peqtub4qjhggaepsq0', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                      body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
                    });
                    const responseText = await res.text();
                    if (res.ok) {
                      try {
                        const parsed = JSON.parse(responseText);
                        if (parsed.code === 'OK') {
                          setFormStatus('success');
                          form.reset();
                        } else {
                          setFormStatus('error');
                          setFormError(parsed?.meta?.message || '提交失败，请稍后再试');
                        }
                      } catch {
                        setFormStatus('success');
                        form.reset();
                      }
                    } else {
                      setFormStatus('error');
                      try {
                        const parsed = JSON.parse(responseText);
                        setFormError(parsed?.meta?.message || parsed?.message || '提交失败，请稍后再试');
                      } catch {
                        setFormError('提交失败，请稍后再试');
                      }
                    }
                  } catch {
                    setFormStatus('error');
                    setFormError('网络错误，请检查您的连接后重试');
                  }
                }}
              >
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="honeypot-field"
                />
                <input
                  type="text"
                  name="name"
                  placeholder="您的姓名"
                  required
                  className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300/60 bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-all"
                />
                <input
                  type="email"
                  name="email"
                  placeholder="您的邮箱"
                  required
                  className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300/60 bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-all"
                />
                <textarea
                  name="message"
                  placeholder="请输入您的留言..."
                  rows={4}
                  maxLength={500}
                  required
                  className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300/60 bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-all resize-none"
                ></textarea>
                <button
                  type="submit"
                  className="whitespace-nowrap w-full py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  发送留言
                </button>

                {formStatus === 'success' && (
                  <div className="p-3 rounded-lg bg-green-50 text-green-700 text-sm text-center">
                    留言已发送，感谢您的反馈！
                  </div>
                )}
                {formStatus === 'error' && (
                  <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm text-center">
                    {formError}
                  </div>
                )}
              </form>
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                常见问题
              </h2>
            </div>

            <div className="flex flex-col gap-3">
              {[
                { q: '网站上的漫画是否完全免费？', a: '是的，韩漫大全网站上的所有韩漫作品全部免费在线阅读，无需付费订阅，也没有任何隐藏费用。我们致力于为广大韩漫爱好者提供一个完全免费的阅读平台。' },
                { q: '网站多久更新一次内容？', a: '韩漫大全网站每天都会更新最新的韩漫内容。连载中的作品会保持与韩国同步的更新频率，确保读者能够在第一时间阅读到最新的章节。' },
                { q: '可以在手机上阅读吗？', a: '当然可以。我们全面适配移动端，您可以使用手机、平板等任何设备随时随地进行阅读，在不同屏幕尺寸上都能获得良好的阅读体验。' },
                { q: '如何找到我想看的韩漫？', a: '您可以通过首页搜索框直接搜索，或通过八大分类浏览筛选，也可以浏览热门推荐、最新更新等板块来发现新作品。' },
                { q: '网站收录了多少部作品？', a: '目前收录超过10,000部韩漫作品，涵盖八大分类，且每天都在持续增加新的作品。' }
              ].map((faq, i) => (
                <details key={i} className="group bg-background-50 rounded-lg overflow-hidden">
                  <summary className="flex items-center justify-between p-4 md:p-5 cursor-pointer list-none">
                    <span className="text-sm font-medium text-foreground-950 pr-4">{faq.q}</span>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform shrink-0"></i>
                  </summary>
                  <div className="px-4 md:px-5 pb-4 md:pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>
    </>
  );
}